export interface ChecklistStep {
  id: string
  text: string
  description?: string
}
export interface Checklist {
  id: string
  title: string
  ward: string
  steps: ChecklistStep[]
}
export const checklists: Checklist[] = [
  // Surgical Ward
  {
    id: 'surgical-site-prep',
    title: 'Surgical Site Preparation',
    ward: 'surgical',
    steps: [
      { id: 's1', text: 'Ensure patient consent form is signed and verified' },
      { id: 's2', text: 'Confirm surgical site and mark appropriately' },
      { id: 's3', text: 'Shave or prepare the surgical area as indicated' },
      { id: 's4', text: 'Perform hand hygiene before site contact' },
      { id: 's5', text: 'Apply antiseptic solution thoroughly to the site' },
      { id: 's6', text: 'Drape the area using sterile sheets' },
      { id: 's7', text: 'Verify all instruments are sterile before incision' },
      { id: 's8', text: 'Maintain sterile field throughout preparation' },
      { id: 's9', text: 'Document preparation and responsible staff' },
    ],
  },
  {
    id: 'pre-operative',
    title: 'Pre-Operative Checklist',
    ward: 'surgical',
    steps: [
      { id: 'p1', text: 'Confirm patient identity and surgical procedure' },
      { id: 'p2', text: 'Verify allergy history and special precautions' },
      { id: 'p3', text: 'Ensure vital signs are stable and recorded' },
      { id: 'p4', text: 'Check fasting status and medication orders' },
      { id: 'p5', text: 'Confirm surgical site marking' },
      { id: 'p6', text: 'Validate anesthesia readiness' },
      { id: 'p7', text: 'Ensure all required equipment is functional' },
      { id: 'p8', text: 'Confirm instrument sterilization status' },
      { id: 'p9', text: 'Obtain team readiness acknowledgment' },
      { id: 'p10', text: 'Record time of surgical transfer' },
    ],
  },
  {
    id: 'post-operative',
    title: 'Post-Operative Monitoring',
    ward: 'surgical',
    steps: [
      { id: 'po1', text: 'Confirm patient identity post-surgery' },
      { id: 'po2', text: 'Assess airway, breathing, and circulation' },
      { id: 'po3', text: 'Monitor pain and administer prescribed analgesics' },
      { id: 'po4', text: 'Record vital signs at regular intervals' },
      { id: 'po5', text: 'Inspect surgical wound dressing for bleeding' },
      { id: 'po6', text: 'Ensure IV lines and drains are functional' },
      { id: 'po7', text: 'Document any complications or abnormal findings' },
      { id: 'po8', text: 'Report recovery progress to attending doctor' },
    ],
  },
  {
    id: 'wound-care',
    title: 'Wound Care Protocol',
    ward: 'surgical',
    steps: [
      { id: 'w1', text: 'Perform hand hygiene before dressing change' },
      { id: 'w2', text: 'Prepare sterile dressing materials' },
      { id: 'w3', text: 'Remove old dressing using aseptic technique' },
      { id: 'w4', text: 'Clean wound using prescribed antiseptic' },
      { id: 'w5', text: 'Apply sterile dressing appropriately' },
      { id: 'w6', text: 'Dispose of used materials safely' },
      { id: 'w7', text: 'Record wound status and procedure time' },
    ],
  },
  {
    id: 'instrument-sterilization',
    title: 'Instrument Sterilization',
    ward: 'surgical',
    steps: [
      { id: 'i1', text: 'Collect used instruments after each procedure' },
      { id: 'i2', text: 'Rinse instruments immediately to remove debris' },
      { id: 'i3', text: 'Place in enzymatic solution for pre-cleaning' },
      { id: 'i4', text: 'Inspect and package for autoclaving' },
      { id: 'i5', text: 'Run sterilization cycle and record indicator change' },
      { id: 'i6', text: 'Store sterile instruments in labeled sterile area' },
    ],
  },
  // Medical Ward
  {
    id: 'patient-admission',
    title: 'Patient Admission',
    ward: 'medical',
    steps: [
      { id: 'pa1', text: 'Verify patient identity and admission request' },
      { id: 'pa2', text: 'Record patient demographic details' },
      { id: 'pa3', text: 'Perform baseline vital signs assessment' },
      { id: 'pa4', text: 'Conduct brief physical examination' },
      { id: 'pa5', text: 'Initiate infection control precautions as required' },
      { id: 'pa6', text: 'Assign bed and label patient chart correctly' },
      { id: 'pa7', text: 'Educate patient on ward hygiene practices' },
      { id: 'pa8', text: 'Document admission time and responsible nurse' },
    ],
  },
  {
    id: 'medication-admin',
    title: 'Medication Administration',
    ward: 'medical',
    steps: [
      {
        id: 'ma1',
        text: 'Verify the five rights (patient, drug, dose, route, time)',
      },
      { id: 'ma2', text: 'Perform hand hygiene before drug preparation' },
      { id: 'ma3', text: 'Check medication expiry and label accuracy' },
      { id: 'ma4', text: 'Prepare drug under aseptic conditions' },
      { id: 'ma5', text: 'Administer medication as prescribed' },
      { id: 'ma6', text: 'Observe patient for reactions' },
      { id: 'ma7', text: 'Dispose sharps and used materials safely' },
      { id: 'ma8', text: 'Document administration time' },
      { id: 'ma9', text: 'Report adverse events immediately' },
    ],
  },
  {
    id: 'vital-signs',
    title: 'Vital Signs Monitoring',
    ward: 'medical',
    steps: [
      { id: 'vs1', text: 'Gather and calibrate monitoring equipment' },
      { id: 'vs2', text: 'Explain procedure to patient' },
      { id: 'vs3', text: 'Record temperature, pulse, respiration, and BP' },
      { id: 'vs4', text: 'Observe for abnormalities and report findings' },
      { id: 'vs5', text: 'Clean and disinfect equipment after use' },
      { id: 'vs6', text: 'Document readings in patient chart' },
    ],
  },
  {
    id: 'discharge-prep',
    title: 'Discharge Preparation',
    ward: 'medical',
    steps: [
      { id: 'dp1', text: 'Confirm discharge order from attending physician' },
      { id: 'dp2', text: 'Review medications and prescriptions with patient' },
      { id: 'dp3', text: 'Provide home care and infection prevention advice' },
      { id: 'dp4', text: 'Return patient belongings' },
      { id: 'dp5', text: 'Remove patient wristband' },
      { id: 'dp6', text: 'Sanitize bed area post-discharge' },
      { id: 'dp7', text: 'Complete discharge documentation' },
    ],
  },
  // A&E Ward
  {
    id: 'triage-assessment',
    title: 'Triage Assessment',
    ward: 'ae',
    steps: [
      { id: 'ta1', text: 'Greet and identify patient' },
      { id: 'ta2', text: 'Assess airway, breathing, and circulation' },
      {
        id: 'ta3',
        text: 'Determine triage category (critical, urgent, stable)',
      },
      { id: 'ta4', text: 'Assign appropriate priority tag' },
      { id: 'ta5', text: 'Record findings in triage log' },
      { id: 'ta6', text: 'Direct patient to appropriate care area' },
    ],
  },
  {
    id: 'trauma-protocol',
    title: 'Trauma Protocol',
    ward: 'ae',
    steps: [
      { id: 'tp1', text: 'Confirm trauma mechanism (e.g., fall, accident)' },
      { id: 'tp2', text: 'Maintain airway and immobilize cervical spine' },
      { id: 'tp3', text: 'Control bleeding using sterile dressing' },
      { id: 'tp4', text: 'Administer oxygen if needed' },
      { id: 'tp5', text: 'Initiate IV access for fluid resuscitation' },
      { id: 'tp6', text: 'Monitor vital signs continuously' },
      { id: 'tp7', text: 'Prepare for transfer to surgery if indicated' },
    ],
  },
  {
    id: 'resuscitation',
    title: 'Resuscitation Checklist',
    ward: 'ae',
    steps: [
      { id: 'r1', text: 'Activate resuscitation team' },
      { id: 'r2', text: 'Check equipment and emergency cart' },
      { id: 'r3', text: 'Assess patient responsiveness' },
      { id: 'r4', text: 'Begin CPR as per guidelines' },
      { id: 'r5', text: 'Establish IV/IO access' },
      { id: 'r6', text: 'Administer emergency drugs as prescribed' },
      { id: 'r7', text: 'Monitor cardiac rhythm' },
      { id: 'r8', text: 'Record all interventions with timestamps' },
      { id: 'r9', text: 'Handover to higher level care if revived' },
    ],
  },
  {
    id: 'emergency-drug',
    title: 'Emergency Drug Administration',
    ward: 'ae',
    steps: [
      { id: 'ed1', text: 'Verify emergency drug order' },
      { id: 'ed2', text: 'Check patient identity' },
      { id: 'ed3', text: 'Administer correct dosage per protocol' },
      { id: 'ed4', text: 'Observe for immediate response' },
      { id: 'ed5', text: 'Document drug, dose, and effect' },
    ],
  },
  // TB Unit
  {
    id: 'tb-screening',
    title: 'TB Screening Protocol',
    ward: 'tb',
    steps: [
      { id: 'tbs1', text: 'Identify suspected TB cases' },
      { id: 'tbs2', text: 'Provide mask and isolate patient immediately' },
      { id: 'tbs3', text: 'Conduct sputum collection per guidelines' },
      { id: 'tbs4', text: 'Label and transport samples to lab' },
      { id: 'tbs5', text: 'Record findings in TB register' },
      { id: 'tbs6', text: 'Notify infection control officer' },
      { id: 'tbs7', text: 'Initiate infection prevention precautions' },
    ],
  },
  {
    id: 'airborne-precautions',
    title: 'Airborne Precautions',
    ward: 'tb',
    steps: [
      { id: 'ap1', text: 'Post airborne precaution signage' },
      { id: 'ap2', text: 'Ensure adequate ventilation or negative pressure' },
      { id: 'ap3', text: 'Use N95 respirator before entry' },
      { id: 'ap4', text: 'Limit staff entry to essential personnel only' },
      { id: 'ap5', text: 'Perform hand hygiene before and after care' },
      { id: 'ap6', text: 'Document compliance in IPC log' },
    ],
  },
  {
    id: 'dot-therapy',
    title: 'DOT Therapy Monitoring',
    ward: 'tb',
    steps: [
      { id: 'dot1', text: 'Verify patient identity and medication schedule' },
      { id: 'dot2', text: 'Observe ingestion of anti-TB drugs directly' },
      { id: 'dot3', text: 'Record adherence daily' },
      { id: 'dot4', text: 'Monitor for side effects' },
      { id: 'dot5', text: 'Reinforce health education' },
      { id: 'dot6', text: 'Submit weekly adherence summary' },
    ],
  },
  {
    id: 'contact-tracing',
    title: 'Contact Tracing',
    ward: 'tb',
    steps: [
      { id: 'ct1', text: 'Identify household or ward contacts' },
      { id: 'ct2', text: 'Conduct TB screening for all exposed' },
      { id: 'ct3', text: 'Record results in contact register' },
      { id: 'ct4', text: 'Educate on infection control practices' },
      { id: 'ct5', text: 'Report findings to TB control team' },
    ],
  },
  // Isolation Unit
  {
    id: 'isolation-setup',
    title: 'Isolation Room Setup',
    ward: 'isolation',
    steps: [
      { id: 'is1', text: 'Designate and label isolation room' },
      { id: 'is2', text: 'Remove unnecessary equipment' },
      { id: 'is3', text: 'Place appropriate signage at entrance' },
      { id: 'is4', text: 'Ensure adequate ventilation' },
      { id: 'is5', text: 'Stock PPE and disinfectants' },
      { id: 'is6', text: 'Check waste disposal containers' },
      { id: 'is7', text: 'Document room readiness' },
    ],
  },
  {
    id: 'ppe-donning',
    title: 'PPE Donning/Doffing',
    ward: 'isolation',
    steps: [
      { id: 'ppe1', text: 'Perform hand hygiene before donning' },
      {
        id: 'ppe2',
        text: 'Wear gown, mask, eye protection, gloves (in order)',
      },
      { id: 'ppe3', text: 'Ensure proper fit and coverage' },
      { id: 'ppe4', text: 'Remove PPE in correct sequence after care' },
      { id: 'ppe5', text: 'Dispose PPE in biohazard bin' },
      { id: 'ppe6', text: 'Perform hand hygiene after removal' },
      { id: 'ppe7', text: 'Record PPE use in log' },
      { id: 'ppe8', text: 'Report shortages immediately' },
    ],
  },
  {
    id: 'specimen-handling',
    title: 'Specimen Handling (Isolation)',
    ward: 'isolation',
    steps: [
      { id: 'sh1', text: 'Label specimen as "Isolation Precaution"' },
      { id: 'sh2', text: 'Wear full PPE before handling' },
      { id: 'sh3', text: 'Collect sample using aseptic technique' },
      { id: 'sh4', text: 'Seal specimen container properly' },
      { id: 'sh5', text: 'Transport using leak-proof bag' },
      { id: 'sh6', text: 'Record collection and transport details' },
    ],
  },
  {
    id: 'waste-disposal',
    title: 'Infectious Waste Disposal',
    ward: 'isolation',
    steps: [
      { id: 'wd1', text: 'Segregate waste per color code' },
      { id: 'wd2', text: 'Use puncture-proof sharps container' },
      { id: 'wd3', text: 'Seal waste bags when ¾ full' },
      { id: 'wd4', text: 'Disinfect outer surfaces before transport' },
      { id: 'wd5', text: 'Log all waste quantities' },
      { id: 'wd6', text: 'Send for incineration or treatment' },
      { id: 'wd7', text: 'Document disposal time and staff ID' },
    ],
  },
  // Labour Ward
  {
    id: 'labour-admission',
    title: 'Labour Admission',
    ward: 'labour',
    steps: [
      { id: 'la1', text: 'Verify admission details and expected delivery' },
      { id: 'la2', text: 'Assess maternal vitals and fetal heartbeat' },
      { id: 'la3', text: 'Record obstetric history' },
      { id: 'la4', text: 'Prepare delivery area and instruments' },
      { id: 'la5', text: 'Apply infection prevention measures' },
      { id: 'la6', text: 'Document admission findings' },
    ],
  },
  {
    id: 'delivery-prep',
    title: 'Delivery Preparation',
    ward: 'labour',
    steps: [
      { id: 'delp1', text: 'Perform hand hygiene and wear sterile gloves' },
      { id: 'delp2', text: 'Prepare sterile delivery pack' },
      { id: 'delp3', text: 'Position mother comfortably' },
      { id: 'delp4', text: 'Check fetal heart rate' },
      { id: 'delp5', text: 'Prepare suction and newborn equipment' },
      { id: 'delp6', text: 'Ensure emergency drugs are available' },
      { id: 'delp7', text: 'Notify attending doctor or midwife' },
    ],
  },
  {
    id: 'newborn-care',
    title: 'Newborn Care Protocol',
    ward: 'labour',
    steps: [
      { id: 'nc1', text: 'Receive baby on warm, sterile surface' },
      { id: 'nc2', text: 'Clear airway and stimulate breathing' },
      { id: 'nc3', text: 'Clamp and cut umbilical cord' },
      { id: 'nc4', text: 'Wipe baby dry and keep warm' },
      { id: 'nc5', text: 'Record Apgar score' },
      { id: 'nc6', text: 'Document newborn details' },
    ],
  },
  {
    id: 'postpartum-care',
    title: 'Postpartum Care',
    ward: 'labour',
    steps: [
      { id: 'ppc1', text: "Assess mother's vital signs" },
      { id: 'ppc2', text: 'Check uterine contraction and bleeding' },
      { id: 'ppc3', text: 'Provide perineal hygiene' },
      { id: 'ppc4', text: 'Encourage breastfeeding' },
      { id: 'ppc5', text: 'Observe for postpartum complications' },
      { id: 'ppc6', text: 'Educate on hygiene and newborn care' },
      { id: 'ppc7', text: 'Document care provided' },
    ],
  },
  // Laboratory Unit
  {
    id: 'specimen-collection',
    title: 'Specimen Collection',
    ward: 'laboratory',
    steps: [
      { id: 'sc1', text: 'Verify test request and patient identity' },
      { id: 'sc2', text: 'Label sample container correctly' },
      { id: 'sc3', text: 'Perform hand hygiene before collection' },
      { id: 'sc4', text: 'Don gloves and protective gear' },
      { id: 'sc5', text: 'Collect sample aseptically' },
      { id: 'sc6', text: 'Seal and transport appropriately' },
      { id: 'sc7', text: 'Dispose sharps safely' },
      { id: 'sc8', text: 'Record collection details' },
    ],
  },
  {
    id: 'blood-culture',
    title: 'Blood Culture Protocol',
    ward: 'laboratory',
    steps: [
      { id: 'bc1', text: 'Prepare sterile culture bottles' },
      { id: 'bc2', text: 'Disinfect puncture site thoroughly' },
      { id: 'bc3', text: 'Draw blood using aseptic technique' },
      { id: 'bc4', text: 'Inoculate bottles immediately' },
      { id: 'bc5', text: 'Label accurately' },
      { id: 'bc6', text: 'Transport to incubation promptly' },
      { id: 'bc7', text: "Document time and collector's name" },
    ],
  },
  {
    id: 'lab-safety',
    title: 'Laboratory Safety Check',
    ward: 'laboratory',
    steps: [
      { id: 'ls1', text: 'Inspect biosafety cabinet for cleanliness' },
      { id: 'ls2', text: 'Verify PPE stock and functionality' },
      { id: 'ls3', text: 'Check waste disposal bins' },
      { id: 'ls4', text: 'Review equipment calibration log' },
      { id: 'ls5', text: 'Ensure emergency eyewash is functional' },
      { id: 'ls6', text: 'Document daily safety verification' },
    ],
  },
  {
    id: 'equipment-calibration',
    title: 'Equipment Calibration',
    ward: 'laboratory',
    steps: [
      { id: 'ec1', text: 'Select instrument for calibration' },
      { id: 'ec2', text: "Use manufacturer's reference material" },
      { id: 'ec3', text: 'Record calibration values' },
      { id: 'ec4', text: 'Validate performance' },
      { id: 'ec5', text: 'Sign off by authorized personnel' },
    ],
  },
]
export const wardNames: Record<string, string> = {
  surgical: 'Surgical Ward',
  medical: 'Medical Ward',
  ae: 'Accident & Emergency (A&E) Ward',
  tb: 'Tuberculosis (TB) Unit',
  isolation: 'Isolation Unit',
  labour: 'Labour Ward',
  laboratory: 'Laboratory Unit',
}
